import { useState, type Dispatch, type SetStateAction } from 'react'
import type { TransactionFilters } from '@/api/transactions'
import type { MembersResponse } from '@/api/members'

export function currentMonth(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export function today(): string {
  return new Date().toISOString().slice(0, 10)
}

export function lastMonth(): string {
  const d = new Date()
  d.setDate(1)
  d.setMonth(d.getMonth() - 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

// Inclusive [dateFrom, dateTo] spanning the current month and the two before it.
export function last3MonthsRange(): { dateFrom: string; dateTo: string } {
  const from = new Date()
  from.setDate(1)
  from.setMonth(from.getMonth() - 2)
  const dateFrom = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-01`
  return { dateFrom, dateTo: today() }
}

export type DatePreset = 'thisMonth' | 'lastMonth' | 'last3Months' | 'custom'

export const DATE_PRESETS: { key: DatePreset; label: string }[] = [
  { key: 'thisMonth', label: 'This Month' },
  { key: 'lastMonth', label: 'Last Month' },
  { key: 'last3Months', label: 'Last 3 Months' },
  { key: 'custom', label: 'Custom' },
]

// Reads the active preset back out of `filters` so the segmented control and the
// filters themselves never drift out of sync with each other.
export function detectPreset(filters: TransactionFilters): DatePreset {
  if (filters.dateFrom || filters.dateTo) {
    if (filters.dateFrom && filters.dateTo) {
      const range = last3MonthsRange()
      return filters.dateFrom === range.dateFrom && filters.dateTo === range.dateTo
        ? 'last3Months'
        : 'custom'
    }
    return 'custom' // "since" (dateFrom only) or "until" (dateTo only)
  }
  if (filters.date) return 'custom'
  if (!filters.month || filters.month === currentMonth()) return 'thisMonth'
  if (filters.month === lastMonth()) return 'lastMonth'
  return 'custom'
}

export type CustomMode = 'month' | 'on' | 'since' | 'between'

export const CUSTOM_MODES: { key: CustomMode; label: string }[] = [
  { key: 'month', label: 'Month' },
  { key: 'on', label: 'On' },
  { key: 'since', label: 'Since' },
  { key: 'between', label: 'Between' },
]

export function detectCustomMode(filters: TransactionFilters): CustomMode {
  if (filters.dateFrom && filters.dateTo) return 'between'
  if (filters.dateFrom) return 'since'
  if (filters.date) return 'on'
  return 'month'
}

/** Flattens users + tracked people into one option list for an "Attributed To" dropdown —
 *  shared by every transaction list's filter bar. */
export function toMemberOptions(members?: MembersResponse): { id: string; label: string }[] {
  return [
    ...(members?.users.map((u) => ({ id: u.id, label: u.displayName ?? u.email })) ?? []),
    ...(members?.trackedPeople.map((t) => ({ id: t.id, label: t.name })) ?? []),
  ]
}

/** One reusable state machine for the date-range portion of a transaction filter bar — the
 *  segmented preset control (This Month / Last Month / Last 3 Months / Custom) plus the Custom
 *  sub-mode (Month / On / Since / Between). Every transaction list (the main ledger, an
 *  account's own transactions, …) shares this, plus <DateRangeFilterFields/>, so the date
 *  filtering UI and behavior never drifts between pages. */
export function useDateRangeFilter(
  filters: TransactionFilters,
  setFilters: Dispatch<SetStateAction<TransactionFilters>>
) {
  const [customOpen, setCustomOpen] = useState(() => detectPreset(filters) === 'custom')
  const [customMode, setCustomMode] = useState<CustomMode>(() => detectCustomMode(filters))
  // customOpen wins even when the underlying filters happen to still match another preset
  // (e.g. switching to Custom without touching Month/Day yet) — it reflects what the user
  // explicitly picked, not just what the filters currently resolve to.
  const activePreset = customOpen ? 'custom' : detectPreset(filters)

  function selectDatePreset(preset: DatePreset) {
    if (preset === 'custom') {
      setCustomOpen(true)
      return
    }
    setCustomOpen(false)
    if (preset === 'thisMonth') {
      setFilters((prev) => ({
        ...prev,
        month: currentMonth(),
        date: undefined,
        dateFrom: undefined,
        dateTo: undefined,
        cursor: undefined,
      }))
    } else if (preset === 'lastMonth') {
      setFilters((prev) => ({
        ...prev,
        month: lastMonth(),
        date: undefined,
        dateFrom: undefined,
        dateTo: undefined,
        cursor: undefined,
      }))
    } else {
      const range = last3MonthsRange()
      setFilters((prev) => ({
        ...prev,
        month: undefined,
        date: undefined,
        dateFrom: range.dateFrom,
        dateTo: range.dateTo,
        cursor: undefined,
      }))
    }
  }

  // Switching Custom sub-mode seeds a sensible default (today) for whichever date field the
  // new mode uses, so the list refetches with something meaningful right away instead of going
  // blank until the user picks a value.
  function selectCustomMode(mode: CustomMode) {
    setCustomMode(mode)
    setFilters((prev) => {
      const base = { ...prev, cursor: undefined }
      if (mode === 'month') {
        return {
          ...base,
          month: prev.month ?? currentMonth(),
          date: undefined,
          dateFrom: undefined,
          dateTo: undefined,
        }
      }
      if (mode === 'on') {
        return {
          ...base,
          date: prev.date ?? today(),
          month: undefined,
          dateFrom: undefined,
          dateTo: undefined,
        }
      }
      if (mode === 'since') {
        return {
          ...base,
          dateFrom: prev.dateFrom ?? today(),
          dateTo: undefined,
          month: undefined,
          date: undefined,
        }
      }
      return {
        ...base,
        dateFrom: prev.dateFrom ?? today(),
        dateTo: prev.dateTo ?? today(),
        month: undefined,
        date: undefined,
      }
    })
  }

  return { activePreset, customOpen, customMode, selectDatePreset, selectCustomMode }
}
